/**
 * CheckoutSessionService
 *
 * Persists checkout session state to Firestore for abandoned cart recovery.
 * All writes are fire-and-forget — they never block the UI.
 *
 * Collection: checkout_sessions/{sessionId}
 */

import { doc, setDoc, updateDoc, DocumentReference } from 'firebase/firestore';
import { db } from '@util/firebase';
import { CheckoutSession } from './types';
import { getAttribution } from '@util/marketing/attribution';
import {
  clearGuestIdentity,
  clearMetaAdvancedMatching,
  isPixelIdentityAttached,
} from '@util/analytics';
import { logError } from '@util/logError';

// ---- Session ID ----

function generateSessionId(): string {
  // Timestamp prefix keeps IDs roughly sortable; random suffix prevents collisions
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 10);
  return `cs_${ts}_${rand}`;
}

// ---- Module-level session ID so updateSession / markConverted can reference it ----

let _currentSessionId: string | null = null;

// ---- Retry helper ----

/**
 * Fire-and-forget Firestore write with one retry on failure.
 * Does NOT block the caller — the retry happens in the background via setTimeout.
 */
async function writeWithRetry(
  ref: DocumentReference,
  data: Record<string, any>,
  operation: 'set' | 'update'
): Promise<void> {
  try {
    if (operation === 'set') {
      await setDoc(ref, data);
    } else {
      await updateDoc(ref, data);
    }
  } catch (err) {
    console.warn('[CheckoutSession] write failed, retrying in 2s:', err);
    setTimeout(async () => {
      try {
        if (operation === 'set') {
          await setDoc(ref, data);
        } else {
          await updateDoc(ref, data);
        }
      } catch (retryErr) {
        console.warn('[CheckoutSession] retry failed:', retryErr);
      }
    }, 2000);
  }
}

// ---- Public API ----

/**
 * Create a new checkout session document in Firestore.
 * Called when the user blurs the email field (early capture) or submits the Information step.
 *
 * RACE SAFETY: _currentSessionId is set SYNCHRONOUSLY before the async Firestore write,
 * so getCurrentSessionId() returns a valid ID immediately — even if the write is still
 * in flight. This prevents duplicate sessions if the user clicks Continue before the
 * initial write completes.
 *
 * Returns the generated session ID synchronously; the Firestore write is background.
 */
export function createSession(
  data: Omit<CheckoutSession, 'id' | 'created_at' | 'updated_at' | 'attribution'>
): string {
  const sessionId = generateSessionId();
  // Set IMMEDIATELY — before the async write — so getCurrentSessionId() is race-safe
  _currentSessionId = sessionId;

  const now = Date.now();
  const attribution = getAttribution() ?? undefined;

  const session: CheckoutSession = {
    ...data,
    id: sessionId,
    created_at: now,
    updated_at: now,
    attribution: attribution as Record<string, string | number | undefined> | undefined,
  };

  // Fire and forget with one retry on failure
  writeWithRetry(doc(db, 'checkout_sessions', sessionId), session as Record<string, any>, 'set');

  return sessionId;
}

/**
 * Update an existing checkout session with new step progress or changed fields.
 * If no sessionId is provided, uses the module-level session ID from createSession.
 */
export function updateSession(
  sessionId: string | null,
  data: Partial<Omit<CheckoutSession, 'id' | 'created_at'>>
): void {
  const id = sessionId ?? _currentSessionId;
  if (!id) {
    console.warn('[CheckoutSession] updateSession called before createSession');
    return;
  }

  const payload = {
    ...data,
    updated_at: Date.now(),
  };

  // Fire and forget with one retry on failure
  writeWithRetry(doc(db, 'checkout_sessions', id), payload as Record<string, any>, 'update');
}

/**
 * Mark the session as converted after a successful order.
 * If no sessionId is provided, uses the module-level session ID from createSession.
 *
 * @param isAuthenticated Whether the purchaser was logged in (pass the
 *   caller's userDoc/user presence at the moment of conversion — callers
 *   should read this from a ref, not a render-time closure, to avoid a stale
 *   value in async confirmation flows). Determines the Advanced Matching
 *   guard below.
 * @returns the `converted: true` Firestore write's promise (F1 fix,
 *   post-merge review, GB #1373). Existing callers that don't care can keep
 *   calling this fire-and-forget, unaffected — adding a return value never
 *   breaks a caller that ignores it. The guest hard-nav path in
 *   CheckoutProcessV3.tsx / ExpressCheckoutButton.tsx now passes this to
 *   google.service.ts's navigateAfterFlush() as an `alsoWaitFor` entry,
 *   which waits for it alongside the purchase dataLayer tags before
 *   navigating, bounded to one shared deadline: previously the write was
 *   truly fire-and-forget with ZERO time to even establish a connection
 *   before window.location.assign() could abort it — writeWithRetry's own
 *   2s-later retry is guaranteed dead once the page has navigated away, so
 *   without this, a guest purchase's checkout_sessions doc could be left
 *   permanently converted:false, and abandoned-cart recovery could email a
 *   PURCHASER. This resolves after the FIRST write attempt settles (success
 *   or failure — writeWithRetry catches internally and never rejects), not
 *   after any scheduled retry, so it stays fast enough for a short race.
 *   KNOWN RESIDUAL (see navigateAfterFlush's doc comment): a genuinely slow
 *   connection can still lose this write, since the shared deadline
 *   navigates regardless of what's still pending — the durable fix is
 *   server-side, filed as a follow-up under GB #1373 / #1129, not built
 *   here.
 */
export async function markConverted(sessionId?: string | null, isAuthenticated?: boolean): Promise<void> {
  const id = sessionId ?? _currentSessionId;
  if (!id) {
    console.warn('[CheckoutSession] markConverted called before createSession');
    return;
  }

  // No longer truly "fire and forget" — see the @returns doc above.
  const writePromise = writeWithRetry(doc(db, 'checkout_sessions', id), {
    converted: true,
    updated_at: Date.now(),
  }, 'update');

  // Clear the module-level session ID after conversion
  _currentSessionId = null;

  // F2 / GB #1373 (QA gate fix, MEDIUM): logPurchase() sets persistent
  // browser pixel Advanced Matching identity. On a shared device / un-closed
  // tab, a purchaser's identity would otherwise keep riding the next
  // shopper's browser events on that same tab.
  //
  // Phase A-E wire evidence (2026-07-15 — see clearMetaAdvancedMatching's
  // doc comment in analytics.service.ts): there is no in-page clear.
  // clearMetaAdvancedMatching() below only cancels a pending retry latch —
  // it CANNOT un-attach an identity that already landed. The real clear for
  // a guest purchaser is the hard navigation shouldHardNavigateOnConversion()
  // signals for: CheckoutProcessV3.tsx and ExpressCheckoutButton.tsx use it
  // to choose window.location.assign(url) over router.push(url) for the
  // post-purchase redirect when a guest's identity actually attached this
  // page. This function's own job is unchanged in shape (still guest-only,
  // still runs before the caller navigates) — only the mechanism downstream
  // of it changed from an (ineffective) in-page clear to a hard nav.
  //
  // Guarded to GUEST purchases only. For an authenticated purchaser,
  // AuthContext is the sole owner of this identity for the rest of their
  // session — it is set once when userDoc resolves and is NOT re-triggered
  // by a purchase, so hard-navigating here would cost that user's own
  // VC/ATC/IC Advanced Matching coverage for no privacy benefit (same
  // person, same identity, nothing to clear). The kiosk/shared-device risk
  // only exists for the anonymous case, where nothing else re-establishes
  // identity — hard-nav there; leave the authenticated case alone.
  if (!isAuthenticated) {
    // Codex re-review fix (P2, GB #1373): isolated in its own try/catch. Both
    // confirmation handlers call markConverted() before navigating to order
    // confirmation — an uncaught throw here would propagate out of
    // markConverted() and could leave a PAID guest stuck on the checkout
    // page with the redirect never reached, same class of bug as the
    // unwrapped logout-path clear fixed earlier. clearMetaAdvancedMatching()
    // itself is now just an interval cancellation (see its doc comment) and
    // essentially can't throw, but the wrapper stays for defense in depth —
    // this is payment-adjacent code and a broken/hostile browser API is not
    // impossible.
    try {
      clearMetaAdvancedMatching();
    } catch (clearError) {
      logError(clearError);
    }
  }

  // TASK-1373 / GB #1206: on a shared device, an un-closed tab left open
  // after checkout would otherwise hand the next shopper the previous
  // shopper's captured email on their own analytics events (the
  // InitiateCheckout gate's fallback paths read getGuestIdentity()).
  // Covers both completion paths — CheckoutProcessV3 and
  // ExpressCheckoutButton both call markConverted() with no arguments.
  // Unconditional (unlike the pixel clear above) — guest identity capture
  // has no authenticated-user analog to protect.
  clearGuestIdentity();

  await writePromise;
}

/**
 * Decides whether the post-purchase redirect to order confirmation must be
 * a HARD navigation (window.location.assign) instead of a client-side
 * router.push. Call this AFTER markConverted() (which itself runs after the
 * Purchase pixel fire — see the ordering note at each call site) and BEFORE
 * navigating, from CheckoutProcessV3.tsx's showConfirmation and
 * ExpressCheckoutButton.tsx's onConfirm.
 *
 * True only for a guest purchaser whose Advanced Matching identity actually
 * attached to the browser pixel this page (isPixelIdentityAttached()) — see
 * clearMetaAdvancedMatching's doc comment in analytics.service.ts for why a
 * hard nav is the only real way to stop that identity riding into whatever
 * anonymous browsing follows on the same tab/device. Authenticated
 * purchasers always get the cheaper SPA nav: AuthContext owns their identity
 * for the whole session, so there is nothing to clear on conversion. Guests
 * whose identity never attached (GPC active, pixel id unset, pixel id
 * drifted) also get the cheaper SPA nav — no identity means nothing to
 * clear, so forcing a full page load would be a pure UX cost with zero
 * privacy benefit.
 *
 * Extracted as a small pure function (rather than inlined at each call site)
 * so this decision is unit-testable without mounting CheckoutProcessV3 or
 * ExpressCheckoutButton, which both carry heavy Stripe/router/Firebase
 * dependencies.
 */
export function shouldHardNavigateOnConversion(isAuthenticated: boolean): boolean {
  return !isAuthenticated && isPixelIdentityAttached();
}

/**
 * Get the current session ID, if one has been created this page lifecycle.
 * Returns non-null as soon as createSession() is called — before the Firestore
 * write completes — so callers can safely check this to avoid duplicate sessions.
 */
export function getCurrentSessionId(): string | null {
  return _currentSessionId;
}

/**
 * Clear the current session ID.
 * Call this on logout so a new session gets a fresh ID on next login.
 */
export function clearSessionId() {
  _currentSessionId = null;
}
